import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Grid,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { 
  collection,
  query,
  getDocs,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { sendEmailNotification, EmailTemplate } from '../../services/emailService';

interface EmailLog {
  id: string;
  to: string;
  subject: string;
  templateName: string;
  status: string;
  timestamp: Timestamp;
  html?: string;
  text?: string;
  templateData?: any;
}

const EmailTester: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [recipient, setRecipient] = useState('');
  const [templateType, setTemplateType] = useState<EmailTemplate>(EmailTemplate.FRIEND_REQUEST);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  
  // Load email logs when component mounts
  useEffect(() => {
    loadEmailLogs();
  }, []);
  
  const loadEmailLogs = async () => {
    if (!currentUser) return;
    
    setLoadingLogs(true);
    try {
      const logsRef = collection(db, 'emailLogs');
      const q = query(logsRef, orderBy('timestamp', 'desc'), limit(20));
      const querySnapshot = await getDocs(q);
      
      const logs: EmailLog[] = [];
      querySnapshot.forEach(doc => {
        logs.push({
          id: doc.id,
          ...doc.data()
        } as EmailLog);
      });
      
      setEmailLogs(logs);
      console.log('Loaded email logs:', logs);
    } catch (error) {
      console.error('Error loading email logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };
  
  const handleSendTestEmail = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setResult(null);
    
    try {
      // Generic test data for each template
      let templateData = {};
      const appUrl = window.location.origin;
      const friendsPageUrl = `${appUrl}/friends`;
      
      switch (templateType) {
        case EmailTemplate.FRIEND_REQUEST:
          templateData = {
            requesterId: currentUser.uid,
            requesterName: 'Test Sender',
            requesterPhotoURL: currentUser.photoURL || '',
            requesterDepartment: 'Test Department',
            recipientName: 'Test Recipient',
            appUrl,
            friendsPageUrl
          };
          break;
        case EmailTemplate.FRIEND_REQUEST_ACCEPTED:
          templateData = {
            acceptorId: currentUser.uid,
            acceptorName: 'Test Acceptor',
            acceptorPhotoURL: currentUser.photoURL || '',
            acceptorDepartment: 'Test Department',
            requesterName: 'Test Requester',
            appUrl,
            friendsPageUrl
          };
          break;
        case EmailTemplate.FRIEND_REQUEST_DECLINED:
          templateData = {
            declinerId: currentUser.uid,
            declinerName: 'Test Decliner',
            requesterName: 'Test Requester',
            appUrl,
            friendsPageUrl
          };
          break;
        default:
          templateData = {
            recipientName: 'Test Recipient',
            appUrl
          };
      }
      
      // Send the test email
      const success = await sendEmailNotification({
        to: recipient,
        subject: `Test Email - ${templateType}`,
        templateName: templateType,
        templateData
      });
      
      if (success) {
        setResult({
          success: true,
          message: `Test email sent successfully to ${recipient}`
        });
        // Refresh email logs after sending
        await loadEmailLogs();
      } else {
        setResult({
          success: false,
          message: 'Failed to send test email'
        });
      }
    } catch (error: any) {
      console.error('Error sending test email:', error);
      setResult({
        success: false,
        message: `Error: ${error.message || 'Unknown error occurred'}`
      });
    } finally {
      setLoading(false);
    }
  };
  
  if (!currentUser) {
    return (
      <Container maxWidth="md">
        <Paper sx={{ p: 4, mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Email Testing Tool
          </Typography>
          <Alert severity="warning">
            You must be logged in to use this tool.
          </Alert>
        </Paper>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 8 }}>
        <Typography variant="h4" gutterBottom>Email Testing Tool</Typography>
        <Typography color="text.secondary" paragraph>
          Use this tool to test email functionality by sending test emails and viewing email logs.
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Send Test Email</Typography>
              
              <Box component="form" sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Recipient Email"
                  variant="outlined"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  margin="normal"
                  required
                />
                
                <FormControl fullWidth margin="normal">
                  <InputLabel>Email Template</InputLabel>
                  <Select
                    value={templateType}
                    label="Email Template"
                    onChange={(e) => setTemplateType(e.target.value as EmailTemplate)}
                  >
                    <MenuItem value={EmailTemplate.FRIEND_REQUEST}>Friend Request</MenuItem>
                    <MenuItem value={EmailTemplate.FRIEND_REQUEST_ACCEPTED}>Friend Request Accepted</MenuItem>
                    <MenuItem value={EmailTemplate.FRIEND_REQUEST_DECLINED}>Friend Request Declined</MenuItem>
                    <MenuItem value={EmailTemplate.WELCOME}>Welcome</MenuItem>
                    <MenuItem value={EmailTemplate.PASSWORD_RESET}>Password Reset</MenuItem>
                  </Select>
                </FormControl>
                
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSendTestEmail}
                  disabled={loading || !recipient}
                  sx={{ mt: 2 }}
                  fullWidth
                >
                  {loading ? <CircularProgress size={24} /> : 'Send Test Email'}
                </Button>
                
                {result && (
                  <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                    {result.message}
                  </Alert>
                )}
              </Box>
            </Paper>
            
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Important Notes</Typography>
              <Typography variant="body2" paragraph>
                • Emails are currently only logged to Firestore, not actually sent.
              </Typography>
              <Typography variant="body2" paragraph>
                • To implement real email sending, you would need to set up Firebase Cloud Functions 
                with an email service like Sendgrid or Mailgun.
              </Typography>
              <Typography variant="body2">
                • Check the browser console for detailed logs about email operations.
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Recent Email Logs</Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={loadEmailLogs}
                  disabled={loadingLogs}
                >
                  {loadingLogs ? <CircularProgress size={20} /> : 'Refresh'}
                </Button>
              </Box>
              
              {loadingLogs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : emailLogs.length === 0 ? (
                <Alert severity="info">No email logs found.</Alert>
              ) : (
                <List>
                  {emailLogs.map((log) => (
                    <Accordion key={log.id} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <Typography variant="subtitle1">{log.subject}</Typography>
                            <Chip 
                              label={log.status} 
                              size="small" 
                              color={log.status === 'sent' ? 'success' : 'error'} 
                              variant="outlined" 
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            To: {log.to} • {log.timestamp?.toDate().toLocaleString() || 'Unknown time'}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="subtitle2" gutterBottom>Template: {log.templateName}</Typography>
                        
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Template Data</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                              {JSON.stringify(log.templateData, null, 2)}
                            </pre>
                          </AccordionDetails>
                        </Accordion>
                        
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>HTML Content</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <iframe
                              title="Email HTML preview"
                              sandbox=""
                              srcDoc={log.html || 'No HTML content'}
                              style={{ width: '100%', minHeight: 200, border: '1px solid #eee' }}
                            />
                          </AccordionDetails>
                        </Accordion>
                        
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Text Content</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                              {log.text || 'No text content'}
                            </pre>
                          </AccordionDetails>
                        </Accordion>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default EmailTester; 